import hexRgb from 'hex-rgb'
import { Node } from 'slate'
import {
  Parser,
  ParserConfig,
  ElementConverter,
  InlineIdentifier,
  RichTextMarkupError
} from '@sm/wufoo-rich-text-markup'
import { getBugsnag } from '../services/telemetry/bugsnag'
import { getSimpleProperty, getTranslatedProperty } from './properties'
import {
  DEFAULT_BUTTON_COLOR,
  DEFAULT_WALLPAPER_COLOR,
  DEFAULT_FONT_FAMILY,
  DEFAULT_FONT_FAMILY_STYLE,
  DEFAULT_FORM_BACKGROUND_COLOR,
  DEFAULT_OPACITY,
  FONT_SIZE_EXTRA_LARGE_SIZE,
  FONT_SIZE_LARGE_SIZE,
  FONT_SIZE_MEDIUM_SIZE,
  FONT_SIZE_SMALL_SIZE,
  QUESTION_TITLE_FONT_SIZES,
} from '../constants/styling'
import {
  FORM_PROPERTY_TYPE_BACKGROUND,
  FORM_PROPERTY_TYPE_WALLPAPER
} from '../constants/formPropertyTypes'
import {
  DROPDOWN_STYLING_ITEMS,
  TOOLBAR_TYPE_STYLING_FONT,
  TOOLBAR_TYPE_STYLING_SIZE
} from '../constants/toolbarTypes'
import {
  SIZE_EXTRA_LARGE_LABEL,
  SIZE_LARGE,
  SIZE_LARGE_LABEL,
  SIZE_MAXIMUM,
  SIZE_MAXIMUM_LABEL,
  SIZE_MEDIUM,
  SIZE_MEDIUM_LABEL,
  SIZE_SMALL,
  SIZE_SMALL_LABEL
} from '../constants/size'

// Create markup parser with configs
const markupParser = new Parser([
  new ParserConfig(1, [
    new ElementConverter(new InlineIdentifier('[', ']', true), 'link')
  ])
])

export const getBackgroundStyles = form => {
  const {
    colour: color,
    fit,
    imageUrl: image,
    isTransparent,
    opacity
  } = getSimpleProperty(form, FORM_PROPERTY_TYPE_BACKGROUND) || {}

  const defaultColor = DEFAULT_FORM_BACKGROUND_COLOR

  let backgroundSize
  if (fit === 'fill') {
    backgroundSize = 'cover'
  } else if (fit === 'tile') {
    backgroundSize = '500px'
  } else if (!fit || fit === 'default') {
    backgroundSize = 'unset'
  } else {
    backgroundSize = ''
  }

  const opacityInPercent = opacity === 0 ? 0 : (opacity || DEFAULT_OPACITY)
  const backgroundColor = getColourWithOpacity(color || defaultColor, opacityInPercent)

  return {
    backgroundColor: backgroundColor,
    backgroundImage: image ? `url(${image})` : '',
    backgroundPosition: 'center center',
    backgroundRepeat: fit === 'tile' ? 'repeat' : 'no-repeat',
    backgroundSize: backgroundSize,
    isTransparent: isTransparent,
    opacity: opacityInPercent / 100
  }
}

export const getWallpaperStyles = form => {
  const {
    colour: color,
    fit,
    imageUrl: image,
    opacity
  } = getSimpleProperty(form, FORM_PROPERTY_TYPE_WALLPAPER) || {}

  const defaultColor = DEFAULT_WALLPAPER_COLOR

  let wallpaperSize
  if (fit === 'fill') {
    wallpaperSize = 'cover'
  } else if (fit === 'tile') {
    wallpaperSize = '500px'
  } else if (!fit || fit === 'default') {
    wallpaperSize = 'unset'
  } else {
    wallpaperSize = ''
  }

  const opacityInPercent = opacity === 0 ? 0 : (opacity || DEFAULT_OPACITY)
  const wallpaperColor = getColourWithOpacity(color || defaultColor, opacityInPercent)

  return {
    backgroundAttachment: fit !== 'tile' ? 'fixed' : '',
    backgroundColor: wallpaperColor || defaultColor,
    backgroundImage: image ? `url(${image})` : '',
    backgroundPosition: 'center center',
    backgroundRepeat: fit === 'tile' ? 'repeat' : 'no-repeat',
    backgroundSize: wallpaperSize,
    opacity: opacityInPercent / 100
  }
}

export const getColourWithOpacity = (color, opacity = DEFAULT_OPACITY) => {
  const { red, green, blue } = hexRgb(color)
  return `rgba(${red}, ${green}, ${blue}, ${opacity/100})`
}

const getButtonSize = (size) => {
  switch (size) {
    case SIZE_MEDIUM:
      return { fontSize: '20px', height: '56px' }
    case SIZE_LARGE:
      return { fontSize: '25px', height: '72px' }
    default:
      return { fontSize: '15px', height: '40px' }
  }
}

export const getButtonStyles = buttonStyles => {
  const {
    backgroundColour = DEFAULT_BUTTON_COLOR,
    shape,
    size
  } = buttonStyles
  const buttonSize = getButtonSize(size)

  return {
    background: backgroundColour,
    borderColor: backgroundColour,
    borderRadius: shape === 'round' ? '20px' : shape === 'semi_round' ? '5px' : '2px',
    ...buttonSize
  }
}

/*
Convert between slate Json value and rich text markup Json value

Slate Json value
[{
  type: 'link',
  children: [{
    text: 'some text',
    bold: 'true'
  }],
  link: 'www.wufoo.com'
}]

Rich text markup Json value
[{
  type: 'link',
  children: [{
    text: 'some text',
    data: {
      bold: 'true'
    }
  }],
  data: {
    link: 'www.wufoo.com'
  }
}]
*/
const getEditorValueFromMarkupJson = markupNodes => {
  return markupNodes.map(node => {
    if ('text' in node) { // Text node
      const { data = {}, ...rest } = node
      return {
        ...rest,
        ...data,
      }
    }
    const { data = {}, children, ...rest } = node
    return {
      ...rest,
      children: getEditorValueFromMarkupJson(children),
      ...data
    }
  })
}

export const getEditorValueFromMarkup = markup => {
  try {
    const markupJson = markupParser.nodesToAst(markupParser.markupToNodes(markup))
    return getEditorValueFromMarkupJson(markupJson)
  } catch (e) {
    if (e instanceof RichTextMarkupError) {
      getBugsnag()?.notify(e) // eslint-disable-line no-unused-expressions
      return [{
        type: 'paragraph',
        children: [{ text: '' }]
      }]
    }
  }
}

export const getFontLabel = (size) => {
  switch (size) {
    case FONT_SIZE_SMALL_SIZE:
      return SIZE_SMALL_LABEL
    case FONT_SIZE_MEDIUM_SIZE:
      return SIZE_MEDIUM_LABEL
    case FONT_SIZE_LARGE_SIZE:
      return SIZE_LARGE_LABEL
    case FONT_SIZE_EXTRA_LARGE_SIZE:
      return SIZE_EXTRA_LARGE_LABEL
    default:
      break
  }
}

export const getLabelFromSizeValue = (size, defaultSize) => {
  switch (size) {
    case SIZE_SMALL:
      return SIZE_SMALL_LABEL
    case SIZE_MEDIUM:
      return SIZE_MEDIUM_LABEL
    case SIZE_LARGE:
      return SIZE_LARGE_LABEL
    case SIZE_MAXIMUM:
      return SIZE_MAXIMUM_LABEL
    default:
      return defaultSize
  }
}

/*
Convert between slate Json value and rich text markup Json value

Slate Json value
[{
  type: 'link',
  children: [{
    text: 'some text',
    bold: 'true'
  }],
  link: 'www.wufoo.com'
}]

Rich text markup Json value
[{
  type: 'link',
  children: [{
    text: 'some text',
    data: {
      bold: 'true'
    }
  }],
  data: {
    link: 'www.wufoo.com'
  }
}]
*/
const getMarkupJsonFromEditorValue = editorNodes => {
  return editorNodes.map(node => {
    if ('text' in node) { // Text node
      const { text, ...data } = node
      return {
        text,
        ...(Object.keys(data).length === 0 ? {}: { data })
      }
    }
    const { type, children, ...data } = node
    return {
      type,
      children: getMarkupJsonFromEditorValue(children),
      ...(Object.keys(data).length === 0 ? {}: { data })
    }
  })
}

export const getMarkupFromEditorValue = editorValue => {
  try {
    const markupJson = getMarkupJsonFromEditorValue(editorValue)
    return markupParser.nodesToMarkup(markupParser.astToNodes(markupJson))
  } catch (e) {
    if (e instanceof RichTextMarkupError) {
      getBugsnag()?.notify(e) // eslint-disable-line no-unused-expressions
      return ''
    }
  }
}

/*
Merge markups from individual option titles into one markup string, joined by '\n'

Options
[{
  translatedValues: {
    'en-US': {
      title: 'v1.First Choice'
    }
  }
}, {
  translatedValues: {
    'en-US': {
      title: 'v1.{Second Choice}(bold)'
    }
  }
}]

Markup output
v1.First Choice\n{Second Choice}(bold)
*/
export const getMarkupFromOptions = (options, language) => {
  const optionsMarkup = options?.reduce((accuMarkup, option, index) => {
    const markup = getTranslatedProperty(option, 'title', language) || ''
    if (index === 0) {
      return accuMarkup + markup
    }
    // remove version prefix from the markup in non-first options
    return accuMarkup + '\n' + markup.replace(/^v([0-9]+)\./, '')
  }, '')
  return optionsMarkup || ''
}

/*
Create options array with markup as option title
Merge properties with existing options if applies

Slate Json value
[{
  type: 'paragraph',
  children: [{
    text: 'option1',
    bold: 'true'
  }]
}, {
  type: 'paragraph',
  children: [{
    text: 'option2',
    color: '#FFFFFF'
  }]
}]

Existing options
[{
  id: 'id1'
}, {
  id: 'id2',
  isInput: true
}]

Output options
[{
  id: 'id1',
  title: 'v1.{option1}(bold)'
}, {
  id: 'id2',
  isInput: true,
  title: 'v1.{option2}(color:#FFFFFF)'
}]
*/
export const getOptionsFromEditorValue = (jsonValue, existingOptions = []) => {
  let options = []
  const isOtherOptionExists = existingOptions[existingOptions.length - 1]?.isInput === true

  for (let i = 0; i < jsonValue.length; i++) {
    // Ignore option with empty text
    if (getPlainTextFromJson([ jsonValue[i] ])) {
      const existingOption = existingOptions[options.length]
      const idProp = existingOption ? { id: existingOption.id } : {}
      const isInputProp = { isInput: isOtherOptionExists && i === jsonValue.length - 1 ? true : false }
      options.push({
        ...idProp,
        ...isInputProp,
        title: getMarkupFromEditorValue([ jsonValue[i] ])
      })
    }
  }
  return options
}

export const getPlainTextFromJson = jsonValue => {
  return (jsonValue?.map(n => Node.string(n)) || []).join('')
}

export const getPlainTextFromMarkup = markup => {
  try {
    return markupParser.nodesToPlainText(markupParser.markupToNodes(markup))
  } catch (e) {
    if (e instanceof RichTextMarkupError) {
      getBugsnag()?.notify(e) // eslint-disable-line no-unused-expressions
      return ''
    }
  }
}

export const getSanitizedBackgroundStyles = (backgroundStyles, wallpaperStyles, showWallpaper) => {
  const { isTransparent, backgroundColor } = backgroundStyles
  const isDefaultBackground = backgroundColor === DEFAULT_FORM_BACKGROUND_COLOR

  if (isTransparent) {
    if (showWallpaper) {
      return {
        backgroundAttachment: null,
        backgroundColor: 'transparent',
        backgroundImage: null,
        backgroundPosition: null,
        backgroundRepeat: null,
        backgroundSize: null,
        opacity: null,
        isDefaultBackground,
        isTransparent
      }
    }
    return {
      ...wallpaperStyles,
      isDefaultBackground,
      isTransparent
    }
  }
  return {
    ...backgroundStyles,
    isDefaultBackground,
    isTransparent
  }
}

export const getStyleFromMarkup = (markup, type) => {
  const valueJson = getEditorValueFromMarkup(markup)
  return getStyleFromEditorValue(valueJson, type)
}

export const getStyleFromEditorValue = (valueJson, type) => {
  let style
  let isFirstTextNode = true
  if (!valueJson) return undefined

  const getStyleRecursive = (valueJson, type) => {
    for (let i = 0; i < valueJson.length; i++) {
      if (style === null) return null
      const node = valueJson[i]
      if ('text' in node) {
        if (isFirstTextNode) {
          style = node[type]
          isFirstTextNode = false
        } else {
          if (style !== node[type]) { // multiple values
            style = null
            break
          }
        }
      }
      if ('children' in node) {
        style = getStyleRecursive(node.children, type, style)
      }
    }
    return style
  }

  return getStyleRecursive(valueJson, type)
}

export const getStyleSelected = (selectedStyle, globalStyle, { defaultStyle, type, shouldUseGlobalStyle = true }) => {
  let globalStyleSanitized = globalStyle
  if (type === TOOLBAR_TYPE_STYLING_SIZE) {
    globalStyleSanitized = QUESTION_TITLE_FONT_SIZES.find(option => option.value === globalStyle)?.size
  } else if (type === TOOLBAR_TYPE_STYLING_FONT) {
    globalStyleSanitized = !globalStyle || globalStyle === DEFAULT_FONT_FAMILY_STYLE ? DEFAULT_FONT_FAMILY : globalStyle
  }

  if (selectedStyle) {
    if (selectedStyle === 'default') {
      return shouldUseGlobalStyle ? globalStyle ? globalStyleSanitized : defaultStyle : defaultStyle
    } else {
      return selectedStyle
    }
  }
  return null
}

export const isDropdownStyling = type => {
  return DROPDOWN_STYLING_ITEMS.includes(type)
}

export const updateStyleInMarkup = (markup, type, value) => {
  const valueJson = getEditorValueFromMarkup(markup)
  const updatedValueJson = updateStyleInEditorValue(valueJson, type, value)
  return getMarkupFromEditorValue(updatedValueJson)
}

const updateStyleInEditorValue = (valueJson, type, value) => {
  return valueJson?.map(node => {
    if ('text' in node) {
      return {
        ...node,
        [type]: value
      }
    } else {
      return {
        ...node,
        children: updateStyleInEditorValue(node?.children, type, value)
      }
    }
  }) || valueJson
}
